import type { FirebasePerformance } from 'firebase/performance';
import { useContext, useEffect, useRef } from 'react';
import { useCookieConsent } from 'web/components/CookieConsentContext';
import errorReporter from 'web/utils/error-reporter';
import { isClientSide } from 'web/utils/ssr';
import { shouldUseEmulators } from './common';
import FirebaseContext from './FirebaseContext';

const isStorybook = process.env.STORYBOOK === 'true';
const initializedPerformance = new Map<string, FirebasePerformance>();
const activePerformanceConsumers = new Map<string, number>();

const setPerformanceEnabled = (appName: string, enabled: boolean) => {
  const performance = initializedPerformance.get(appName);
  if (performance) {
    performance.dataCollectionEnabled = enabled;
    performance.instrumentationEnabled = enabled;
  }
};

const usePerformance = () => {
  const firebase = useContext(FirebaseContext);
  const { measurementAllowed } = useCookieConsent();
  const measurementEnabled = isClientSide && !shouldUseEmulators && !isStorybook && measurementAllowed;
  const measurementEnabledRef = useRef(measurementEnabled);
  measurementEnabledRef.current = measurementEnabled;

  useEffect(() => {
    const existingPerformance = initializedPerformance.get(firebase.name);
    if (!measurementEnabled) {
      if (!activePerformanceConsumers.get(firebase.name)) {
        setPerformanceEnabled(firebase.name, false);
      }
      return;
    }

    activePerformanceConsumers.set(firebase.name, (activePerformanceConsumers.get(firebase.name) || 0) + 1);
    let active = true;
    const release = () => {
      active = false;
      const remainingConsumers = Math.max(0, (activePerformanceConsumers.get(firebase.name) || 1) - 1);
      if (remainingConsumers === 0) {
        activePerformanceConsumers.delete(firebase.name);
        setPerformanceEnabled(firebase.name, false);
      } else {
        activePerformanceConsumers.set(firebase.name, remainingConsumers);
      }
    };

    if (existingPerformance) {
      setPerformanceEnabled(firebase.name, true);
      return release;
    }

    void import('firebase/performance')
      .then(({ initializePerformance }) => {
        if (!active || !measurementEnabledRef.current || initializedPerformance.has(firebase.name)) {
          return;
        }

        try {
          initializedPerformance.set(
            firebase.name,
            initializePerformance(firebase, {
              dataCollectionEnabled: true,
              instrumentationEnabled: true,
            }),
          );
        } catch (err) {
          errorReporter.report(new Error(`Failed to initialize firebase performance: ${err}`));
        }
      })
      .catch((err) => {
        if (active) {
          errorReporter.report(new Error(`Failed to load firebase performance: ${err}`));
        }
      });

    return release;
  }, [firebase, measurementEnabled]);
};

export default usePerformance;
